/**
 * Notifications redesign (Sept 2026) — the numbers and the pure logic. Nothing here touches
 * the DOM: the row, the dropdown and the tests all read it.
 * Handoff: ~/Downloads/design_handoff_notifications/README.md.
 */
import { RING, fillFor } from '../wall/wallTokens';
import { familyOf, typeOf, faceTint } from './notificationTypes';

export { RING, fillFor };

export const T = {
  canvas: '#F5F5F5',
  white: '#FFFFFF',
  off: '#F2F2F2',
  ink: '#000000',
  grey600: '#4B5563',
  grey500: '#6B7280',
  grey400: '#9CA3AF',
  live: '#FF00AA',
  signal: '#00FFFF',
  neg: '#EF4444',
  pos: '#22C55E',
  /** A read card sits back into the canvas. */
  readCard: 'rgba(255,255,255,.6)',
};

export const RADIUS = { pill: 999, panel: 24, bubble: 20, row: 18 };

export const QUOTE_FONT = "'Times New Roman', Times, serif";
export const BUBBLE_FONT = 'Arial, sans-serif';

// ── Time ─────────────────────────────────────────────────────────────────────────────

/** "JUST NOW" · "5M AGO" · "3H AGO" · "2D AGO" · "SEP 3". */
export function ago(iso, now = new Date()) {
  const date = new Date(iso);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'JUST NOW';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}M AGO`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}H AGO`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}D AGO`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  }).toUpperCase();
}

/** "3H 12M LEFT" · "58M LEFT"; null once it's gone (or was never timed). */
export function timeLeft(iso, now = new Date()) {
  if (!iso) return null;
  const ms = new Date(iso) - now;
  if (ms <= 0) return null;
  const mins = Math.floor(ms / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h >= 24) return `${Math.floor(h / 24)}D ${h % 24}H LEFT`;
  if (h > 0) return `${h}H ${m}M LEFT`;
  return `${Math.max(1, m)}M LEFT`;
}

export const DAY_LABELS = ['TODAY', 'YESTERDAY', 'EARLIER THIS WEEK', 'EARLIER'];

/** Which day group a timestamp files under, on the viewer's clock. */
export function dayLabel(iso, now = new Date()) {
  const d = new Date(iso);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (d >= startOfToday) return 'TODAY';
  if (d >= new Date(startOfToday.getTime() - 86400000)) return 'YESTERDAY';
  if (d >= new Date(startOfToday.getTime() - 6 * 86400000)) return 'EARLIER THIS WEEK';
  return 'EARLIER';
}

/** [{ label, items, count }] in DAY_LABELS order, empty groups dropped. */
export function groupByDay(items, now = new Date()) {
  const buckets = new Map(DAY_LABELS.map((l) => [l, []]));
  for (const n of items) buckets.get(dayLabel(n.createdAt, now)).push(n);
  return DAY_LABELS
    .map((label) => {
      const list = buckets.get(label);
      return { label, items: list, count: `${list.length} ${list.length === 1 ? 'ITEM' : 'ITEMS'}` };
    })
    .filter((g) => g.items.length);
}

// ── Decoration ───────────────────────────────────────────────────────────────────────

const PORTAL_OPEN_TYPES = new Set(['SignalReceived', 'PortalSignalReceived']);
const QUOTE_TYPES = new Set(['SignalReceived', 'PortalSignalReceived', 'SignalMention', 'PortalSignalInvited', 'ClubPollCreated']);
const BUBBLE_TYPES = new Set(['MessageReceived', 'MessageMention', 'MessageReaction']);

/** "Reza Houshmand opened a Portal" → "Reza Houshmand" (rows written before the rename say "sent a signal"). */
function nameFromTitle(title) {
  const m = /^(.+?) (opened a portal|sent a signal|is in|sent you|wants to|tagged you)/i.exec(title || '');
  return m ? m[1] : null;
}

/** What the row can do about it — see NotificationActions for what each kind renders. */
export function actionKindFor(n) {
  const isHostRow = n.type === 'PortalSignalAccepted' || n.type === 'PortalSignalApplicationReceived';
  if (n.portal && !isHostRow && (PORTAL_OPEN_TYPES.has(n.type) || n.type === 'SignalMention')) return 'portal';
  if (n.type === 'PortalSignalInvited' && n.portal) return 'invite';
  switch (n.type) {
    case 'FriendRequestReceived': return 'friend';
    case 'IntroductionReceived': return 'intro';
    case 'ProOfferCountered':
    case 'ProOfferReceived': return 'offer';
    case 'WaitlistSpotAvailable': return 'spot';
    case 'PartyEngineClaimInvite': return 'claim';
    case 'PortalSignalAccepted':
    case 'PortalSignalApplicationReceived':
    case 'MessageReceived': return 'chat';
    case 'MessageMention':
    case 'MessageReaction':
    case 'ForumReply':
    case 'ForumMention':
    case 'ClubAnnouncementReply':
    case 'PortalSignalReplied': return 'reply';
    case 'ClubPollCreated': return 'vote';
    case 'WithdrawalFailed': return 'payout';
    default: return null;
  }
}

/** The dollar figure in a counter-offer's message: "$450.00 → $520.00 …" → "$520.00". */
export function offerAmount(message) {
  const all = (message || '').match(/\$\d[\d,]*(?:\.\d{2})?/g);
  return all ? all[all.length - 1] : null;
}

/**
 * One API row → everything the row and the dropdown draw. `now` is injectable for tests.
 */
export function decorate(n, now = new Date()) {
  const t = typeOf(n.type, n.family);
  const f = familyOf(t.family) || {};
  const neg = t.sev === 'neg';
  const unread = !n.isRead;
  const actor = n.actor || null;
  const portal = n.portal || null;

  const isPortal = PORTAL_OPEN_TYPES.has(n.type);
  const name = actor?.displayName || nameFromTitle(n.title);

  // Title
  let title = n.title;
  if (isPortal && name) title = `${name} opened a portal`;
  else if (n.type === 'PortalSignalAccepted' && actor) title = `${actor.displayName} is in`;
  else if (n.type === 'PortalSignalApplicationReceived' && actor) title = `${actor.displayName} wants in`;

  // Body: quote (the Portal's words, a poll) · bubble (a message) · plain (a fact).
  let body = n.message || '';
  let variant = 'plain';
  if (QUOTE_TYPES.has(n.type)) {
    variant = 'quote';
    if (portal?.text) body = portal.text;
  } else if (BUBBLE_TYPES.has(n.type)) {
    variant = 'bubble';
  } else if ((n.type === 'PortalSignalAccepted' || n.type === 'PortalSignalApplicationReceived') && portal?.text) {
    body = `For your portal: ${portal.text.replace(/[.!?]$/, '')}.`;
  }
  if (!body) variant = 'none';

  // A secure thread's notification (Oct 2026) names nobody and quotes nothing: the row
  // wears a padlock before the title instead of a face.
  const secure = BUBBLE_TYPES.has(n.type) && !actor && /^you received /i.test(n.title || '');
  if (secure) variant = 'none';

  // Face: the actor's photo or initial; a brand logo / hero stands in when there's no person.
  let face = null;
  if (actor) face = { img: actor.avatarUrl || null, initial: (actor.displayName || '?')[0], tint: faceTint(actor.userId) };
  else if (n.imageUrl && n.type !== 'AdminAnnouncement') face = { img: n.imageUrl, initial: '' };

  // The Portal ring: spinning while it's live and unread, dimmed once it's closed.
  let live;
  if (f.frame === 'ring' && portal) live = portal.isClosed ? false : unread;
  else if (f.frame === 'ring') live = undefined;

  const pillBg = neg ? T.neg : (t.family === 'signals' || t.family === 'host') ? T.ink : (f.bg || T.off);
  const pillFg = neg ? '#fff' : t.family === 'signals' ? T.signal : t.family === 'host' ? '#fff' : '#000';

  return {
    ...n,
    unread,
    t,
    /** The family row (label, tint, frame); `family` stays the API's key string. */
    fam: f,
    label: t.label || n.typeDisplay || '',
    isPortal,
    name,
    title,
    body,
    variant,
    face,
    secure,
    live,
    left: unread && portal && !portal.isClosed ? timeLeft(portal.expiresAt, now) : null,
    hero: n.type === 'AdminAnnouncement' ? n.imageUrl || null : null,
    pillBg,
    pillFg,
    ago: ago(n.createdAt, now),
    actionKind: actionKindFor(n),
    /** The dropdown's one-line preview keeps the body's voice. */
    preview: variant === 'quote' ? `“${body}”` : body,
  };
}
